import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import { FormikContext } from "formik";

import MuiBox from "../MuiBox";
import MuiTypography from "../MuiTypography";
import FormError from "../FormError";

import VisiblityIcon from "assets/images/svg-icons/eye-icon.svg";
import VisiblityOffIcon from "assets/images/svg-icons/eye-slash-icon.svg";

const Input = forwardRef(
  (
    {
      label,
      className,
      onChange,
      onBlur,
      onFocus,
      type = "text",
      value,
      error,
      errorMsg,
      hideErrorOnFocus = true,
      placeholder = "Enter Here",
      name,
      fullWidth = true,
      disabled = false,
      variant = "light",
      rows = 3,
      multiline = false,
      required = false,
      isNumeric = false,
      maxLength = 10,
      maxCharacter = 100000,
      isTextOnly = false,
      isSpace = false,
      isBorderColor = false,
      isLowercase = false,
      isRegexMail = false,
      isAlphaNumericWithSpecial = false,
      ...rest
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showError, setShowError] = useState(false);

    const formikContext = useContext(FormikContext);

    const isInFormik = typeof formikContext?.setFieldError === "function";

    const submitCount = formikContext?.submitCount ?? 0;
    const isValidating = formikContext?.isValidating ?? false;

    const lastHandledSubmitRef = useRef(submitCount);
    const errorShownForSubmitRef = useRef(false);

    /**
     * Handle error visibility.
     *
     * Outside Formik:
     * - Error visibility directly follows the `error` prop.
     *
     * Inside Formik:
     * - Errors are handled around submit/validation state.
     * - Existing errors are displayed after validation.
     * - Errors are reset when there is no error.
     */
    useEffect(() => {
      if (!isInFormik) {
        setShowError(Boolean(error));
        return;
      }

      if (!error) {
        errorShownForSubmitRef.current = false;
        setShowError(false);
        return;
      }

      if (submitCount !== lastHandledSubmitRef.current) {
        lastHandledSubmitRef.current = submitCount;

        if (isValidating) {
          errorShownForSubmitRef.current = false;
          setShowError(false);
          return;
        }

        if (error) {
          errorShownForSubmitRef.current = true;
          setShowError(true);
          return;
        }

        return;
      }

      if (!errorShownForSubmitRef.current) {
        errorShownForSubmitRef.current = true;
        setShowError(true);
      }
    }, [submitCount, error, isValidating, isInFormik]);

    /**
     * Handle input focus.
     *
     * When the user focuses an input:
     * - Hide the current error UI.
     * - Clear the Formik field error.
     * - Reset Formik touched state.
     * - Execute the parent's onFocus callback.
     */
    const handleFocus = (e) => {
      if (!hideErrorOnFocus) {
        onFocus?.(e);
        return;
      }

      setShowError(false);
      errorShownForSubmitRef.current = false;

      if (isInFormik && name) {
        formikContext.setFieldError(name, undefined);
        formikContext.setFieldTouched(name, false, false);
      }

      if (onFocus) {
        onFocus(e);
      }
    };

    let { sx, helperText, ...restProps } = rest;

    const handleInputChange = (e) => {
      let inputValue = e.target.value;

      if (isLowercase) {
        inputValue = inputValue.toLowerCase();
      }

      if (maxCharacter) {
        inputValue = inputValue.slice(0, maxCharacter);
      }

      if (isRegexMail) {
        inputValue = inputValue.replace(/[^a-z0-9._]/gi, "");

        const forbiddenSuffixes = [".com", ".net", ".org", ".in"];

        forbiddenSuffixes.forEach((suffix) => {
          if (inputValue.toLowerCase().endsWith(suffix)) {
            inputValue = inputValue.slice(0, inputValue.length - 1);
          }
        });
      }

      if (isNumeric) {
        inputValue = inputValue.replace(/\D/g, "").slice(0, maxLength);
      }

      if (isTextOnly) {
        inputValue = inputValue.replace(/[^a-zA-Z\s]/g, "");
      }

      if (isAlphaNumericWithSpecial) {
        // keep only allowed characters
        inputValue = inputValue.replace(/[^a-zA-Z0-9._ -]/g, "");
        // collapse any consecutive hyphens into a single one
        inputValue = inputValue.replace(/-{2,}/g, "-");
      }

      if (isSpace) {
        inputValue = inputValue.replace(/^\s+/, "");
      }

      onChange({
        target: {
          name,
          value: inputValue,
        },
      });
    };

    const handleClickShowPassword = () => {
      if (type === "password") {
        setShowPassword((prev) => !prev);
      }
    };

    return (
      <MuiBox sx={{ opacity: disabled ? "0.5" : "1" }}>
        <MuiBox sx={{ position: "relative" }}>
          {label && (
            <MuiBox
              sx={{
                mb: "6px",
              }}
            >
              <MuiTypography
                variant="16"
                fontWeight={400}
                color="text.darkBlack"
                sx={{
                  display: "flex",
                  gap: "5px",
                }}
              >
                {label}

                <MuiTypography
                  sx={{
                    color: "#FF4D4D !important",
                  }}
                >
                  {required && " *"}
                </MuiTypography>
              </MuiTypography>
            </MuiBox>
          )}

          <FormControl
            fullWidth={fullWidth}
            error={Boolean(showError)}
            sx={{
              "& .MuiFormHelperText-root": {
                marginLeft: "0px !important",
              },
            }}
          >
            <OutlinedInput
              inputRef={ref}
              variant={variant}
              id={name}
              multiline={multiline}
              rows={rows}
              className={className}
              onChange={handleInputChange}
              onBlur={onBlur}
              onFocus={handleFocus}
              type={showPassword ? "text" : type}
              value={value}
              placeholder={placeholder}
              autoComplete="new-password"
              name={name}
              disabled={disabled}
              endAdornment={
                type === "password" && (
                  <InputAdornment position="end">
                    <IconButton
                      sx={{
                        color: "button.white",
                        m: 0,
                        mr: "5px",
                      }}
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      <img
                        height="20px"
                        width="20px"
                        src={showPassword ? VisiblityIcon : VisiblityOffIcon}
                        alt={showPassword ? "Hide password" : "Show password"}
                        style={{
                          filter:
                            variant === "dark"
                              ? "invert(1) brightness(20)"
                              : "",
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }
              sx={{
                p: 0,

                "& .MuiInputBase-input": {
                  p: "10px 12px",
                  fontSize: "14px",
                  fontFamily: "Inter",
                  color: "#000",
                },

                "& textarea": {
                  padding: "10px 12px",
                },

                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid !important",

                  borderColor: showError
                    ? "#E12121 !important"
                    : isBorderColor
                      ? "#E6E6E6 !important"
                      : "transparent !important",

                  boxShadow: showError
                    ? "0px 0px 0px 2px #FFC9C9"
                    : "transparent !important",
                },

                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: showError
                    ? "#E12121 !important"
                    : "#1D4ED8 !important",

                  boxShadow: showError
                    ? "0px 0px 0px 2px #FFC9C9"
                    : "0px 0px 0px 2px #DBEAFE",
                },

                bgcolor: "transparent",

                ...sx,
              }}
              {...restProps}
            />

            {!showError && helperText && (
              <FormHelperText>
                <MuiTypography
                  variant="12"
                  sx={{
                    color: "#A3A2A6",
                  }}
                >
                  {helperText}
                </MuiTypography>
              </FormHelperText>
            )}
          </FormControl>

          {showError && errorMsg && <FormError errorMsg={errorMsg} />}
        </MuiBox>
      </MuiBox>
    );
  },
);

Input.displayName = "Input";

export default Input;
